"use strict";
// Copyright © Veeam Software Group GmbH
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    Object.defineProperty(o, k2, { enumerable: true, get: function() { return m[k]; } });
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Wizard = void 0;
const react_1 = __importStar(require("react"));
const rxjs_1 = require("rxjs");
const components_1 = require("@veeam/components");
const helpers_1 = require("./helpers");
const WizardDialog_1 = require("./components/WizardDialog");
const WizardPage_1 = require("./components/WizardPage");
const types_1 = require("./types");
exports.Wizard = react_1.default.forwardRef(({ buttons, canSave, canCancel = true, hasKeyboardInteractive = true, hasTopPadding = true, header, height = types_1.WIZARD_SIZE.m, id, initialStepIdx = 0, loading, onDone, onExitPage, onRequestClose, onStepChange, onKeyPress, pageDescription, pageTitle, pageHeaderControls, panel, contentMinWidth, panelWidth, panelMinWidth, tabWidth, steps, type = types_1.WIZARD_TYPE.dialog, width = types_1.WIZARD_SIZE.m, hasBack = true, apiController, style, actionsStyle, }, ref) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
    for (let idx = initialStepIdx; idx < steps.length; idx++) {
        if (!steps[idx].hidden && !steps[idx].disabled) {
            initialStepIdx = idx;
            break;
        }
    }
    const [currentStepIdx, setStep] = react_1.useState(initialStepIdx);
    const { getValue: getSteps } = components_1.useStateValue(steps, [steps], {
        depsComparator: helpers_1.stepsComparator,
    });
    const currentStep = steps[currentStepIdx];
    if (!currentStep) {
        return null;
    }
    const [enableStepsSchema, setEnableStepsSchema] = react_1.useState(helpers_1.createEnableStepsSchema(getSteps()));
    react_1.useEffect(() => {
        const stepFormObservers = getSteps()
            .map((step) => step.form && step.form.asObserver())
            .filter(Boolean);
        const subscription = rxjs_1.combineLatest(stepFormObservers).subscribe(() => {
            setEnableStepsSchema(helpers_1.createEnableStepsSchema(getSteps()));
        });
        return () => subscription.unsubscribe();
    }, []);
    const hasPrevStep = () => {
        let idx = currentStepIdx - 1;
        for (; idx >= 0; idx--) {
            if (!steps[idx].disabled && !steps[idx].hidden) {
                break;
            }
        }
        return !!steps[idx] && !steps[idx].disabled;
    };
    const nextStep = steps[currentStepIdx + 1];
    const validateStep = (idx) => __awaiter(void 0, void 0, void 0, function* () {
        let isValid = true;
        const step = steps[idx];
        if (step.hidden || step.disabled) {
            return true;
        }
        if (step.form && step.form.submit) {
            try {
                yield step.form.submit();
                isValid = step.form.isValid();
            }
            catch (e) {
                isValid = false;
            }
        }
        if (isValid && step.validate) {
            isValid = isValid && (yield step.validate());
        }
        return isValid;
    });
    const validateSteps = (startIdx, endIdx) => __awaiter(void 0, void 0, void 0, function* () {
        let isValid = true;
        for (let i = startIdx; i <= endIdx; i++) {
            isValid = yield validateStep(i);
            if (!isValid) {
                setStep(i);
                break;
            }
        }
        return isValid;
    });
    const selectHandler = (selectIdx, direction = 0) => __awaiter(void 0, void 0, void 0, function* () {
        let canSelect = true;
        if (steps[selectIdx].disabled) {
            // next button or prev pressed, but needed step disabled
            let idx = selectIdx + direction;
            for (; idx < steps.length && idx >= 0; idx += direction) {
                if (!steps[idx].disabled) {
                    selectIdx = idx;
                    break;
                }
            }
            if (idx === steps.length || idx < 0) {
                return;
            }
        }
        if (selectIdx > currentStepIdx) {
            canSelect = yield validateSteps(currentStepIdx, selectIdx - 1);
        }
        if (!canSelect) {
            return;
        }
        setStep(selectIdx);
        if (onStepChange) {
            onStepChange(steps[selectIdx].tag, steps[currentStepIdx].tag);
        }
    });
    const nextHandler = () => __awaiter(void 0, void 0, void 0, function* () {
        const nextIdx = currentStepIdx + 1;
        yield selectHandler(nextIdx, 1);
    });
    const prevHandler = () => __awaiter(void 0, void 0, void 0, function* () {
        const prevIdx = currentStepIdx - 1;
        yield selectHandler(prevIdx, -1);
    });
    const doneHandler = react_1.useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        let isValid = true;
        if (canSave) {
            if (currentStepIdx < getSteps().length - 1) {
                isValid = yield validateSteps(currentStepIdx, getSteps().length - 1); // last (summary) step should be validated, too
            }
        }
        if (isValid && onDone) {
            onDone();
        }
    }), [currentStepIdx, onStepChange]);
    const enterKeyHandler = () => {
        if (!hasKeyboardInteractive || loading) {
            return;
        }
        if (nextStep) {
            nextHandler();
            return;
        }
        doneHandler();
    };
    const switchStepByTag = react_1.useCallback((tag) => __awaiter(void 0, void 0, void 0, function* () {
        const idx = getSteps().findIndex((item) => item.tag === tag);
        if (idx === -1 || idx === currentStepIdx) {
            return;
        }
        const direction = idx > currentStepIdx ? 1 : -1;
        return selectHandler(idx, direction);
    }), [currentStepIdx]);
    const wizardApi = {
        switchStepByTag,
    };
    if (apiController) {
        apiController.injectApi(wizardApi);
    }
    react_1.useEffect(() => {
        return () => {
            apiController === null || apiController === void 0 ? void 0 : apiController.destroy();
        };
    }, []);
    const actions = [
        hasPrevStep() && {
            id: helpers_1.WIZARD_ACTIONS_ID.prev,
            view: components_1.ACTION_VIEW.alternate,
            text: (_b = (_a = buttons === null || buttons === void 0 ? void 0 : buttons.previous) === null || _a === void 0 ? void 0 : _a.label) !== null && _b !== void 0 ? _b : 'Previous',
            disabled: (_c = buttons === null || buttons === void 0 ? void 0 : buttons.previous) === null || _c === void 0 ? void 0 : _c.disabled,
            onClick: prevHandler,
        },
        !!nextStep && {
            id: helpers_1.WIZARD_ACTIONS_ID.next,
            view: components_1.ACTION_VIEW.primary,
            text: (_e = (_d = buttons === null || buttons === void 0 ? void 0 : buttons.next) === null || _d === void 0 ? void 0 : _d.label) !== null && _e !== void 0 ? _e : 'Next',
            disabled: (_f = buttons === null || buttons === void 0 ? void 0 : buttons.next) === null || _f === void 0 ? void 0 : _f.disabled,
            onClick: nextHandler,
        },
        (!nextStep || canSave) && {
            id: helpers_1.WIZARD_ACTIONS_ID.done,
            view: components_1.ACTION_VIEW.primary,
            text: (_h = (_g = buttons === null || buttons === void 0 ? void 0 : buttons.done) === null || _g === void 0 ? void 0 : _g.label) !== null && _h !== void 0 ? _h : 'Finish',
            disabled: (_j = buttons === null || buttons === void 0 ? void 0 : buttons.done) === null || _j === void 0 ? void 0 : _j.disabled,
            onClick: doneHandler,
        },
        canCancel && {
            id: helpers_1.WIZARD_ACTIONS_ID.cancel,
            view: components_1.ACTION_VIEW.secondary,
            text: (_l = (_k = buttons === null || buttons === void 0 ? void 0 : buttons.cancel) === null || _k === void 0 ? void 0 : _k.label) !== null && _l !== void 0 ? _l : 'Cancel',
            disabled: (_m = buttons === null || buttons === void 0 ? void 0 : buttons.cancel) === null || _m === void 0 ? void 0 : _m.disabled,
            onClick: onRequestClose,
        },
    ].filter(Boolean);
    const commonProps = {
        id,
        style,
        loading,
        hasTopPadding,
        currentStep,
        currentStepIdx,
        enableStepsSchema,
        selectHandler,
        onKeyPress: (e) => {
            if (e.keyCode === components_1.KeyCode.Enter) {
                enterKeyHandler();
            }
            onKeyPress === null || onKeyPress === void 0 ? void 0 : onKeyPress(e);
        },
        actions,
        steps,
        ref,
    };
    const dialogProps = Object.assign(Object.assign({}, commonProps), { header,
        height,
        width,
        onRequestClose });
    const pageProps = Object.assign(Object.assign({}, commonProps), { onExitPage,
        pageDescription,
        pageTitle,
        pageHeaderControls,
        panel,
        contentMinWidth,
        panelWidth,
        panelMinWidth,
        tabWidth,
        hasBack,
        actionsStyle });
    return type === types_1.WIZARD_TYPE.dialog ? (react_1.default.createElement(WizardDialog_1.WizardDialog, Object.assign({}, dialogProps))) : (react_1.default.createElement(WizardPage_1.WizardPage, Object.assign({}, pageProps)));
});
exports.Wizard.displayName = 'Wizard';
//# sourceMappingURL=Wizard.js.map